import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ImagePlus, Loader2, Plus, Ruler, Send, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useLogoUpload } from "../lib/useLogoUpload";
import { lp } from "../lib/localize";
import { FadeUp } from "../components/motion";
import { SEO } from "../components/SEO";
import { formatPhone } from "../lib/utils";  // format auto (123) 456-7890 (règle 2026-10-22)

// Page vitrine « Plaquettes sur mesure » (demande du propriétaire) : exemples
// réels de la boutique + formulaire de soumission détaillé (dimensions, couleur,
// matériau Métal/ABS/Vinyle, Graver/Imprimer, lignes de texte extensibles, logo).
// La demande part dans les Soumissions de l'admin + courriel à l'atelier.
const inputCls = "w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300";
const labelCls = "block text-xs uppercase tracking-widest text-ivory/60 mb-1.5";

function txt(lang) {
  const fr = lang !== "en";
  const L = (a, b) => (fr ? a : b);
  return {
    fr, L,
    seo_t: L("Gravure sur mesure | Mr Trophée", "Custom engraving | Mr Trophée"),
    seo_d: L("Gravure sur mesure : plaquettes, signalétique et plaques gravées ou imprimées — métal, ABS ou vinyle, vos dimensions, votre texte, votre logo. Atelier à Léry, Québec.",
             "Custom engraving: engraved or printed plaques, signs and plates — metal, ABS or vinyl, your size, your text, your logo. Workshop in Léry, Quebec."),
    surtitre: L("Sur mesure", "Made to measure"),
    titreA: L("Gravure ", ""), titreB: L("sur mesure", "Custom engraving"),
    intro: L("Porte de bureau, signalétique, mur de reconnaissance, plaque commémorative — dites-nous les dimensions, la couleur et le texte : nous gravons ou imprimons votre pièce à l'atelier, à Léry.",
             "Office door sign, signage, recognition wall, commemorative plaque — tell us the size, colour and text: we engrave or print your piece in our Léry workshop."),
    exemples: L("Exemples de l'atelier", "From the workshop"),
    form_t: L("Décrivez votre gravure", "Describe your engraving"),
    form_s: L("Réponse rapide avec prix et disponibilité — sans obligation.", "Quick reply with pricing and availability — no obligation."),
    longueur: L("Longueur", "Length"), largeur: L("Largeur", "Width"),
    unite: L("Unité", "Unit"),
    couleur: L("Couleur de la plaquette", "Plaque colour"),
    couleur_autre: L("Précisez la couleur ou la demande spéciale", "Specify the colour or special request"),
    couleur_autre_ph: L("Ex. : vert forêt, cuivre antique…", "E.g.: forest green, antique copper…"),
    couleurs: [  // couleurs de plaquette de l'atelier (demande du propriétaire) — valeurs FR pour l'atelier
      ["Or mat", "Matte gold"], ["Or brillant", "Bright gold"],
      ["Argent mat", "Matte silver"], ["Argent brillant", "Bright silver"],
      ["Bronze", "Bronze"],
      ["Rouge métallique", "Metallic red"], ["Bleu métallique", "Metallic blue"],
      ["Rouge", "Red"], ["Noir brillant", "Glossy black"], ["Noir mat", "Matte black"],
      ["Autre couleur du catalogue ou demande spéciale", "Other catalogue colour or special request"],
    ],
    materiau: L("Matériau", "Material"), methode: L("Finition", "Finish"),
    finitions: [  // finitions de plaquette de l'atelier (demande du propriétaire) — valeurs FR pour l'atelier
      ["Gravée Laser", "Laser engraved"],
      ["Gravée Pointe Diamant", "Diamond-point engraved"],
      ["Imprimée Sérigraphie", "Screen printed"],
    ],
    materiau_autre: L("Précisez le matériau", "Specify the material"),
    materiau_autre_ph: L("Ex. : acier inoxydable, acrylique…", "E.g.: stainless steel, acrylic…"),
    materiaux: [  // matériaux de plaquette de l'atelier (demande du propriétaire) — valeurs FR pour l'atelier
      ["Aluminium (le plus courant)", "Aluminum (most common)"],
      ["Laiton (plus dispendieux)", "Brass (more expensive)"],
      ["ABS", "ABS"],
      ["Vinyle", "Vinyl"],
      ["Cuir", "Leather"],
      ["Bois de noyer", "Walnut wood"],
      ["Autre (à préciser)", "Other (specify)"],
    ],
    qty: L("Quantité", "Quantity"),
    texte_t: L("Quoi écrire", "What to write"),
    ligne: L("Ligne", "Line"),
    ajouter_ligne: L("Ajouter une ligne", "Add a line"),
    logo: L("Ajouter un logo ? (optionnel)", "Add a logo? (optional)"),
    logo_btn: L("Téléverser un logo", "Upload a logo"),
    notes: L("Notes (optionnel)", "Notes (optional)"),
    notes_ph: L("Précisions, police souhaitée, délai…", "Details, preferred font, deadline…"),
    prenom: L("Prénom", "First name"), nom: L("Nom", "Last name"), org: L("Organisation (optionnel)", "Organization (optional)"),
    adresse: L("Adresse (numéro et rue)", "Address (street no. and name)"),
    ville: L("Ville", "City"), code_postal: L("Code postal", "Postal code"), tel: L("Téléphone", "Phone"), courriel: L("Courriel", "Email"),
    matrice: L("Frais de matrice : à déterminer — confirmés dans votre soumission (gravure et impression couleur aussi : à déterminer).",
               "Die (matrix) fee: to be determined — confirmed in your quote (engraving and colour printing also: to be determined)."),
    date: L("Livrer avant le (optionnel)", "Needed by (optional)"),
    envoyer: L("Envoyer ma demande", "Send my request"),
    ok_t: L("Demande envoyée !", "Request sent!"),
    ok_s: L("Nous vous répondrons rapidement avec prix et disponibilité. Une confirmation vient de partir dans votre boîte courriel.", "We'll reply quickly with pricing and availability. A confirmation is on its way to your inbox."),
    err_dim: L("Inscrivez la longueur et la largeur.", "Please enter the length and width."),
    err_txt: L("Inscrivez au moins une ligne de texte (ou précisez dans les notes).", "Enter at least one line of text (or specify in the notes)."),
    voir: L("Voir la fiche", "View product"),
    dès: L("dès", "from"),
  };
}

export default function PlaqueSurMesure() {
  const { lang } = useLang();
  const c = txt(lang);
  const [exemples, setExemples] = useState([]);
  const [form, setForm] = useState({ first_name: "", last_name: "", organization: "", address1: "", city: "", postal_code: "", phone: "", email: "", needed_by: "" });
  const [specs, setSpecs] = useState({ longueur: "", largeur: "", unite: "po", couleur: "", couleur_autre: "", materiau: "Aluminium (le plus courant)", materiau_autre: "", methode: "Gravée Laser", qty: 1, notes: "" });
  const [lignes, setLignes] = useState(["", "", ""]);
  const [loading, setLoading] = useState(false);
  const [envoye, setEnvoye] = useState(false);
  const { logo, setLogo, uploading, fileInput, uploadLogo } = useLogoUpload((k) => k);
  const { user } = useAuth();
  const prefilledRef = useRef(false);

  // Préremplissage client inscrit (demande du propriétaire 2026-10-09) — sans
  // jamais écraser un champ déjà modifié à la main
  useEffect(() => {
    if (!user || prefilledRef.current) return;
    prefilledRef.current = true;
    const parts = (user.name || "").trim().split(/\s+/);
    setForm((f) => ({
      ...f,
      first_name: f.first_name || user.first_name || parts[0] || "",
      last_name: f.last_name || user.last_name || parts.slice(1).join(" ") || "",
      email: f.email || user.email || "",
      phone: f.phone || user.phone || "",
      address1: f.address1 || user.address1 || "",
      city: f.city || user.city || "",
      postal_code: f.postal_code || user.postal_code || "",
    }));
    api.get("/account/profile").then(({ data }) => {
      const a0 = (data.addresses || [])[0];
      setForm((f) => ({
        ...f,
        phone: f.phone || data.phone || a0?.phone || "",
        address1: f.address1 || a0?.address1 || "",
        city: f.city || a0?.city || "",
        postal_code: f.postal_code || a0?.postal_code || "",
      }));
    }).catch(() => {});
  }, [user]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "phone" ? formatPhone(e.target.value) : e.target.value }));
  const setS = (k) => (e) => setSpecs((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {  // exemples réels : la catégorie « Plaques » — 6 choisis AU HASARD à chaque visite (demande du propriétaire)
    api.get("/products/browse", { params: { shop_category: "Plaques", limit: 60 } })
      .then(({ data }) => {
        const pool = (data.products || data.items || []).filter((p) => p.image);
        for (let i = pool.length - 1; i > 0; i--) {  // brassage Fisher-Yates
          const j = Math.floor(Math.random() * (i + 1));
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        setExemples(pool.slice(0, 6));
      })
      .catch(() => {});
  }, []);

  const details = useMemo(() => {
    const L = lignes.map((x, i) => x.trim() && `${c.ligne} ${i + 1} : ${x.trim()}`).filter(Boolean).join(" / ");
    const coul = specs.couleur.startsWith("Autre couleur") && (specs.couleur_autre || "").trim()
      ? `${specs.couleur} : ${specs.couleur_autre.trim()}` : specs.couleur;
    const mat = specs.materiau.startsWith("Autre") && (specs.materiau_autre || "").trim()
      ? `${specs.materiau} : ${specs.materiau_autre.trim()}` : specs.materiau;
    const parts = [
      `${c.L("Dimensions", "Size")} : ${specs.longueur || "?"} × ${specs.largeur || "?"} ${specs.unite}`,
      `${c.L("Couleur", "Colour")} : ${coul || "?"}`,
      mat, specs.methode, L && `${c.L("Texte", "Text")} — ${L}`,
      specs.notes.trim() && `${c.L("Notes", "Notes")} : ${specs.notes.trim()}`,
    ].filter(Boolean);
    return parts.join(" — ").slice(0, 590);
  }, [specs, lignes, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!specs.longueur.trim() || !specs.largeur.trim()) { toast.error(c.err_dim); return; }
    if (!lignes.some((x) => x.trim()) && !specs.notes.trim()) { toast.error(c.err_txt); return; }
    setLoading(true);
    try {
      await api.post("/quick-quote", {
        ...form, lang,
        items: [{
          catalogue: c.L("Gravure sur mesure", "Custom engraving"),
          product_name: `${c.L("Gravure sur mesure", "Custom engraving")} ${specs.longueur}×${specs.largeur} ${specs.unite}`.slice(0, 120),
          product_no: "", qty: Math.max(1, parseInt(specs.qty, 10) || 1),
          details, logo_path: logo?.path || "",
        }],
      });
      setEnvoye(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="plaquettes-page" className="max-w-5xl mx-auto px-6 lg:px-10 pt-32 pb-28">
      <SEO title={c.seo_t} description={c.seo_d} path="/plaquettes-sur-mesure" />
      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{c.surtitre}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-5">
          {c.titreA}<span className="text-gold italic">{c.titreB}</span>
        </h1>
        <p className="text-ivory/60 text-base max-w-2xl mb-14">{c.intro}</p>
      </FadeUp>

      <FadeUp>
        <h2 className="font-serif text-2xl mb-1">{c.form_t}</h2>
        <p className="text-sm text-ivory/50 mb-6">{c.form_s}</p>
        {envoye ? (
          <div className="border border-gold/40 rounded-2xl p-10 text-center" data-testid="plaquette-sent">
            <h3 className="font-serif text-3xl text-gold mb-3">{c.ok_t}</h3>
            <p className="text-ivory/70 text-sm">{c.ok_s}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="plaquette-form">
            {/* Spécifications de la plaquette */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <div className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
                  <div>
                    <label className={labelCls} htmlFor="pm-longueur">{c.longueur} *</label>
                    <div className="relative">
                      <Ruler className="w-4 h-4 text-ivory/30 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input id="pm-longueur" data-testid="plaquette-longueur" value={specs.longueur} onChange={setS("longueur")}
                        maxLength={20} placeholder="8" className={`${inputCls} pl-10`} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="pm-largeur">{c.largeur} *</label>
                    <div className="relative">
                      <Ruler className="w-4 h-4 text-ivory/30 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input id="pm-largeur" data-testid="plaquette-largeur" value={specs.largeur} onChange={setS("largeur")}
                        maxLength={20} placeholder="2" className={`${inputCls} pl-10`} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{c.unite}</label>
                    <div className="flex rounded-lg overflow-hidden border border-ivory/15" data-testid="plaquette-unite">
                      {["po", "mm"].map((u) => (
                        <button key={u} type="button" onClick={() => setSpecs((f) => ({ ...f, unite: u }))}
                          data-testid={`plaquette-unite-${u}`}
                          className={`px-4 py-3 text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${specs.unite === u ? "bg-gold text-ink" : "bg-ink text-ivory/50 hover:text-gold"}`}>
                          {u}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="pm-couleur">{c.couleur}</label>
                <select id="pm-couleur" data-testid="plaquette-couleur" value={specs.couleur} onChange={setS("couleur")} className={inputCls}>
                  <option value="">—</option>
                  {c.couleurs.map(([frV, enV]) => (
                    <option key={frV} value={frV}>{c.L(frV, enV)}</option>
                  ))}
                </select>
              </div>
              {specs.couleur.startsWith("Autre couleur") && (
                <div>
                  <label className={labelCls} htmlFor="pm-couleur-autre">{c.couleur_autre}</label>
                  <input id="pm-couleur-autre" data-testid="plaquette-couleur-autre" value={specs.couleur_autre || ""}
                    onChange={setS("couleur_autre")} maxLength={80} placeholder={c.couleur_autre_ph} className={inputCls} />
                </div>
              )}
              <div>
                <label className={labelCls} htmlFor="pm-qty">{c.qty}</label>
                <input id="pm-qty" data-testid="plaquette-qty" type="number" min="1" value={specs.qty} onChange={setS("qty")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="pm-materiau">{c.materiau}</label>
                <select id="pm-materiau" data-testid="plaquette-materiau" value={specs.materiau} onChange={setS("materiau")} className={inputCls}>
                  {c.materiaux.map(([frV, enV]) => (
                    <option key={frV} value={frV}>{c.L(frV, enV)}</option>
                  ))}
                </select>
              </div>
              {specs.materiau.startsWith("Autre") && (
                <div>
                  <label className={labelCls} htmlFor="pm-materiau-autre">{c.materiau_autre}</label>
                  <input id="pm-materiau-autre" data-testid="plaquette-materiau-autre" value={specs.materiau_autre || ""}
                    onChange={setS("materiau_autre")} maxLength={80} placeholder={c.materiau_autre_ph} className={inputCls} />
                </div>
              )}
              <div>
                <label className={labelCls} htmlFor="pm-methode">{c.methode}</label>
                <select id="pm-methode" data-testid="plaquette-methode" value={specs.methode} onChange={setS("methode")} className={inputCls}>
                  {c.finitions.map(([frV, enV]) => (
                    <option key={frV} value={frV}>{c.L(frV, enV)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Lignes de texte extensibles */}
            <div>
              <p className={labelCls}>{c.texte_t}</p>
              <div className="space-y-2">
                {lignes.map((l, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={l} data-testid={`plaquette-ligne-${i + 1}`} maxLength={80}
                      onChange={(e) => setLignes((xs) => xs.map((x, j) => (j === i ? e.target.value : x)))}
                      placeholder={`${c.ligne} ${i + 1}`} className={inputCls} />
                    {lignes.length > 1 && (
                      <button type="button" data-testid={`plaquette-ligne-del-${i + 1}`}
                        onClick={() => setLignes((xs) => xs.filter((_, j) => j !== i))}
                        className="shrink-0 w-11 rounded-lg border border-ivory/15 text-ivory/40 hover:text-red-400 hover:border-red-400/40 transition-colors duration-300 flex items-center justify-center">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {lignes.length < 8 && (
                <button type="button" data-testid="plaquette-ligne-add"
                  onClick={() => setLignes((xs) => [...xs, ""])}
                  className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold hover:text-champagne transition-colors duration-300">
                  <Plus className="w-4 h-4" /> {c.ajouter_ligne}
                </button>
              )}
            </div>

            {/* Logo */}
            <div>
              <p className={labelCls}>{c.logo}</p>
              <input type="file" accept="image/*" ref={fileInput} className="hidden" onChange={uploadLogo} data-testid="plaquette-logo-input" />
              {logo ? (
                <div className="flex items-center gap-3 bg-ink border border-gold/30 rounded-lg px-4 py-2.5" data-testid="plaquette-logo-ok">
                  <img src={logo.url} alt={logo.name} className="w-10 h-10 object-contain bg-white rounded" />
                  <span className="text-sm text-ivory/75 flex-1 truncate">{logo.name}</span>
                  <button type="button" onClick={() => setLogo(null)} data-testid="plaquette-logo-del"
                    className="text-ivory/40 hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button type="button" onClick={() => fileInput.current?.click()} disabled={uploading}
                  data-testid="plaquette-logo-btn"
                  className="inline-flex items-center gap-2 border border-dashed border-ivory/25 rounded-lg px-5 py-3 text-sm text-ivory/65 hover:border-gold hover:text-gold transition-colors duration-300">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {c.logo_btn}
                </button>
              )}
            </div>

            {/* Coordonnées */}
            <div className="grid sm:grid-cols-2 gap-3 border-t border-ivory/10 pt-6">
              <div><label className={labelCls}>{c.prenom} *</label>
                <input required value={form.first_name} onChange={set("first_name")} maxLength={60} data-testid="plaquette-first-name" className={inputCls} /></div>
              <div><label className={labelCls}>{c.nom} *</label>
                <input required value={form.last_name} onChange={set("last_name")} maxLength={60} data-testid="plaquette-last-name" className={inputCls} /></div>
              <div><label className={labelCls}>{c.org}</label>
                <input value={form.organization} onChange={set("organization")} maxLength={120} data-testid="plaquette-organization" className={inputCls} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>{c.adresse} *</label>
                <input required minLength={3} value={form.address1} onChange={set("address1")} maxLength={200} data-testid="plaquette-address" className={inputCls} /></div>
              <div><label className={labelCls}>{c.ville} *</label>
                <input required minLength={2} value={form.city} onChange={set("city")} maxLength={100} data-testid="plaquette-city" className={inputCls} /></div>
              <div><label className={labelCls}>{c.code_postal} *</label>
                <input required minLength={6} maxLength={7} placeholder="A1A 1A1" value={form.postal_code} onChange={set("postal_code")} data-testid="plaquette-postal-code" className={inputCls} /></div>
              <div><label className={labelCls}>{c.tel} *</label>
                <input required type="tel" value={form.phone} onChange={set("phone")} maxLength={30} data-testid="plaquette-phone" className={inputCls} /></div>
              <div><label className={labelCls}>{c.courriel} *</label>
                <input required type="email" value={form.email} onChange={set("email")} maxLength={120} data-testid="plaquette-email" className={inputCls} /></div>
              <div><label className={labelCls}>{c.date}</label>
                <input type="date" value={form.needed_by} onChange={set("needed_by")} data-testid="plaquette-needed-by" className={inputCls} /></div>
              <div><label className={labelCls}>{c.notes}</label>
                <input value={specs.notes} onChange={setS("notes")} maxLength={200} placeholder={c.notes_ph} data-testid="plaquette-notes" className={inputCls} /></div>
            </div>

            {/* Frais de matrice « à déterminer » (demande du propriétaire 2026-10-09) */}
            <p className="text-xs text-gold/90 bg-gold/5 border border-gold/25 rounded-lg px-4 py-3"
              data-testid="plaquette-matrice-note">
              {c.matrice}
            </p>

            <button type="submit" disabled={loading} data-testid="plaquette-submit"
              className="inline-flex items-center gap-2.5 bg-gold text-ink font-semibold rounded-full px-8 py-3.5 text-sm uppercase tracking-widest hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {c.envoyer}
            </button>
          </form>
        )}
      </FadeUp>

      {/* Exemples APRÈS le formulaire, choisis au hasard à chaque visite (demande du propriétaire) */}
      {exemples.length > 0 && (
        <FadeUp>
          <h2 className="font-serif text-2xl mt-16 mb-6">{c.exemples}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="plaquettes-exemples">
            {exemples.map((p) => (
              <Link key={p.id} to={`/produit/${p.id}`} data-testid={`plaquette-ex-${p.id}`}
                className="group bg-ink border border-ivory/10 rounded-xl overflow-hidden hover:border-gold/40 transition-colors duration-300">
                <img src={p.image} alt={lp(p, lang).name} loading="lazy" className="w-full h-40 object-contain bg-[#f4f2ec]" />
                <div className="p-3">
                  <p className="text-sm text-ivory/85 leading-snug line-clamp-2">{lp(p, lang).name}</p>
                  <p className="text-xs text-gold mt-1">{c.dès} {(p.base_price || 0).toFixed(2)} $</p>
                </div>
              </Link>
            ))}
          </div>
        </FadeUp>
      )}
    </div>
  );
}
